import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import { cn } from "utils/cn";
import { askAiTemplateAgent } from "services/aiAgentService";
import { truncateMarkdownForNeoAi } from "utils/neoAiApplyEdit";
import { computeProposedMarkdown } from "utils/neoAiProposedMarkdown";
import { createNeoAiUndoStack } from "utils/neoAiEditorUndo";
import {
  applyNeoAiAssistantResult,
  neoAiTemplateApplySuccessHint,
} from "utils/neoAiApplyAssistantResult";
import NeoAiEditPreviewModal from "components/neo-ai/NeoAiEditPreviewModal";
import { useTemplateEditor } from "../context/TemplateEditorContext";

const INITIAL_ASSISTANT_MESSAGE =
  "Olá! Sou o NeoAi. Ajudo você a elaborar modelos de documentos.\n\n" +
  "Descreva o documento que deseja (ex.: «Elabore um modelo sobre compra e venda de imóveis»). " +
  "Eu gero o texto com campos variáveis entre chaves, como {NOME}, {CPF} ou {VALOR}.\n\n" +
  "Com conteúdo no editor, posso ajustar cláusulas ou inserir na posição do cursor. " +
  "Editor vazio: o modelo gerado é inserido no cursor.";

const NeoAiTemplateChatPanel = ({ onNeoCoinBalanceUpdate }) => {
  const { editor, syncFieldsFromEditorContent } = useTemplateEditor();
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState(() => [
    { role: "assistant", text: INITIAL_ASSISTANT_MESSAGE },
  ]);
  const [loading, setLoading] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const listRef = useRef(null);
  const streamingAbortRef = useRef(null);
  const undoStackRef = useRef(createNeoAiUndoStack());

  const syncTemplateFieldsFromEditor = useCallback(() => {
    window.setTimeout(() => {
      syncFieldsFromEditorContent(true);
    }, 0);
  }, [syncFieldsFromEditorContent]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open, loading, inserting]);

  useEffect(() => {
    return () => {
      streamingAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDownOutside = (event) => {
      const root = panelRef.current;
      if (!root) return;
      if (root.contains(event.target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    document.addEventListener("touchstart", handlePointerDownOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside);
      document.removeEventListener("touchstart", handlePointerDownOutside);
    };
  }, [open]);

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const handleUndo = useCallback(() => {
    if (!editor) return;
    const ok = undoStackRef.current.undo(editor);
    setCanUndo(undoStackRef.current.canUndo());
    if (ok) {
      syncTemplateFieldsFromEditor();
      appendMessage({
        role: "assistant",
        text: "Última alteração da IA desfeita no editor.",
      });
    }
  }, [appendMessage, editor, syncTemplateFieldsFromEditor]);

  const handleConfirmPreview = useCallback(async () => {
    if (!previewPayload || !editor) return;
    const { result, documentMarkdownSent, cursorPos } = previewPayload;
    streamingAbortRef.current?.abort();
    streamingAbortRef.current = new AbortController();
    const { signal } = streamingAbortRef.current;

    setInserting(true);
    try {
      const applied = await applyNeoAiAssistantResult({
        editor,
        result,
        documentMarkdownSent,
        cursorPos,
        signal,
        undoStack: undoStackRef.current,
      });
      setPreviewOpen(false);
      setPreviewPayload(null);
      if (!applied.ok) {
        appendMessage({ role: "assistant", text: applied.userMessage });
        setCanUndo(undoStackRef.current.canUndo());
        return;
      }
      syncTemplateFieldsFromEditor();
      setCanUndo(undoStackRef.current.canUndo());
      appendMessage({
        role: "assistant",
        text: `Pronto! ${neoAiTemplateApplySuccessHint(applied.mode)} Campos entre chaves foram detectados no painel lateral.`,
      });
    } catch (err) {
      console.error("NeoAi template apply:", err);
      appendMessage({
        role: "assistant",
        text: "Erro ao aplicar no editor. Tente novamente ou desfaça.",
      });
    } finally {
      setInserting(false);
    }
  }, [appendMessage, editor, previewPayload, syncTemplateFieldsFromEditor]);

  const handleSend = useCallback(async () => {
    const prompt = input.trim();
    if (!prompt || loading || inserting) return;

    const cursorPos =
      typeof editor?.state?.selection?.from === "number"
        ? editor.state.selection.from
        : null;

    const rawMd =
      editor && typeof editor.getMarkdown === "function" ? editor.getMarkdown() : "";
    const documentMarkdownSent = rawMd.trim()
      ? truncateMarkdownForNeoAi(rawMd.trim())
      : undefined;

    setInput("");
    appendMessage({ role: "user", text: prompt });
    streamingAbortRef.current?.abort();
    streamingAbortRef.current = new AbortController();
    const { signal } = streamingAbortRef.current;

    setLoading(true);

    try {
      const result = await askAiTemplateAgent({
        prompt,
        documentMarkdown: documentMarkdownSent,
      });
      setLoading(false);

      if (!result.ok) {
        appendMessage({
          role: "assistant",
          text: result.message || "Não foi possível concluir o pedido.",
        });
        return;
      }

      if (result.coinBalance !== undefined && result.coinBalance !== null) {
        onNeoCoinBalanceUpdate?.(result.coinBalance);
      }

      const responseMd = typeof result.response === "string" ? result.response.trim() : "";
      const editMarkdown =
        result.edit && typeof result.edit.markdown === "string"
          ? result.edit.markdown.trim()
          : "";
      if (!result.edit && !responseMd) {
        appendMessage({
          role: "assistant",
          text: "O assistente não retornou texto para inserir.",
        });
        return;
      }
      if (result.edit && !editMarkdown) {
        appendMessage({
          role: "assistant",
          text: "O assistente não retornou conteúdo editável.",
        });
        return;
      }

      if (!editor) {
        appendMessage({
          role: "assistant",
          text: "Editor indisponível; não foi possível inserir o texto.",
        });
        return;
      }

      const currentMd =
        typeof editor.getMarkdown === "function" ? editor.getMarkdown() : "";
      const preview = computeProposedMarkdown({
        documentMarkdownSent: documentMarkdownSent ?? "",
        edit: result.edit,
        responseMd,
        currentMarkdown: currentMd,
      });

      setPreviewPayload({
        result,
        documentMarkdownSent: documentMarkdownSent ?? "",
        cursorPos: cursorPos ?? editor.state.selection.from,
        preview,
      });
      setPreviewOpen(true);
    } catch (err) {
      console.error("NeoAi ask-template:", err);
      setLoading(false);
      appendMessage({
        role: "assistant",
        text: "Erro ao contatar o assistente. Tente novamente.",
      });
    }
  }, [
    appendMessage,
    editor,
    input,
    inserting,
    loading,
    onNeoCoinBalanceUpdate,
    syncTemplateFieldsFromEditor,
  ]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      ref={panelRef}
      className={cn(
        "relative flex flex-col items-start",
        open && "w-[min(22rem,calc(100vw-1.5rem))]"
      )}
    >
      {open ? (
        <div className="flex flex-col max-h-[min(70vh,28rem)] rounded-xl border border-border bg-card overflow-hidden shadow-elevation-lg">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-primary text-primary-foreground shrink-0">
            <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
              <Icon name="Sparkles" size={18} />
              NeoAi · Modelos
            </span>
            <div className="flex items-center gap-1">
              {canUndo ? (
                <button
                  type="button"
                  onClick={handleUndo}
                  disabled={loading || inserting}
                  className="rounded-md px-2 py-1 text-xs font-medium hover:bg-primary-foreground/15"
                >
                  Desfazer IA
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 hover:bg-primary-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/40"
                aria-label="Recolher NeoAi"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>

          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-3 py-2 space-y-3 min-h-[180px] max-h-[min(42vh,18rem)] bg-muted/20"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={cn(
                  "text-sm rounded-lg px-3 py-2 leading-relaxed",
                  m.role === "user"
                    ? "bg-primary/10 text-foreground ml-4"
                    : "bg-background border border-border mr-2"
                )}
              >
                <span className="whitespace-pre-wrap">{m.text}</span>
              </div>
            ))}
            <AnimatePresence mode="sync">
              {loading && (
                <motion.div
                  key="neoai-template-loading"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border bg-background/95 text-sm text-muted-foreground"
                >
                  <span
                    className="h-4 w-4 shrink-0 rounded-full border-2 border-muted border-t-primary animate-spin"
                    aria-hidden
                  />
                  <span className="tracking-tight">Elaborando seu modelo…</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="p-2 border-t border-border bg-card shrink-0">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={2}
              disabled={loading || inserting || !editor}
              placeholder={
                editor
                  ? "Ex.: Elabore um modelo sobre compra e venda de imóveis…"
                  : "Aguarde o editor carregar…"
              }
              className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={loading || inserting || !input.trim() || !editor}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Icon name="Send" size={16} />
              <span className="hidden sm:inline">Enviar (contexto do modelo)</span>
              <span className="sm:hidden">Enviar</span>
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-elevation-md text-sm font-semibold text-foreground hover:bg-muted transition-colors"
          aria-label="Abrir NeoAi para modelos"
        >
          <Icon name="Sparkles" size={20} className="text-primary" />
          NeoAi
        </button>
      )}

      <NeoAiEditPreviewModal
        open={previewOpen}
        onClose={() => {
          if (inserting) return;
          setPreviewOpen(false);
          setPreviewPayload(null);
          appendMessage({
            role: "assistant",
            text: "Alteração descartada. Neo Coins já foram consumidos neste pedido.",
          });
        }}
        onConfirm={handleConfirmPreview}
        before={previewPayload?.preview?.before}
        after={previewPayload?.preview?.after}
        changedSegmentBefore={previewPayload?.preview?.changedSegmentBefore}
        changedSegmentAfter={previewPayload?.preview?.changedSegmentAfter}
        incomingOnly={previewPayload?.preview?.incomingOnly}
        summary={previewPayload?.preview?.summary}
        applying={inserting}
      />
    </div>
  );
};

export default NeoAiTemplateChatPanel;
